import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PlebData } from '../../models/Pleb';
import { Credentials } from '../../models/Credentials';

export enum AuthenticationStatus {
  INITIAL = 'initial',
  LOGGING_OUT = 'logging_out',
  AUTHENTICATED = 'authenticated',
  UNAUTHENTICATED = 'unauthenticated'
}

export enum AuthenticationStatusGetStream {
  UNAUTHENTICATED = 'unauthenticated',
  LOADING = 'loading',
  LOGGED = 'logged',
  ERROR = 'error'
}

interface UserAuthData {
  pleb: PlebData;
  tokens: Credentials;
}

const LOCAL_STORAGE_KEY_LAST_USER = 'lastUserId';

// Local storage helper functions
const LOCAL_STORAGE_KEY = 'userAuthData';

const saveUserToStorage = (userId: string, data: UserAuthData) => {
  const existingData = localStorage.getItem(LOCAL_STORAGE_KEY);
  const allUsersData = existingData ? JSON.parse(existingData) : {};
  allUsersData[userId] = data;
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(allUsersData));
};

const removeUserFromStorage = (userId: string) => {
  const existingData = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (existingData) {
    const allUsersData = JSON.parse(existingData);
    delete allUsersData[userId];
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(allUsersData));
  }
};

const loadUsersFromStorage = (): Record<string, UserAuthData> => {
  const data = localStorage.getItem(LOCAL_STORAGE_KEY);
  return data ? JSON.parse(data) : {};
};

export interface CallStreamData {
  id: string;
  name: string;
  image: string;
  userId: string;
}

interface AuthState {
  status: AuthenticationStatus;
  users: Record<string, PlebData>;
  tokens: Record<string, Credentials>;
  currentUserId: string | null;
  nbUnreadByUser: Record<string, number>;
  statusGetStream: AuthenticationStatusGetStream;
  currentCallData: CallStreamData | null;
  idsCallsIgnored: string[];
}

const initialState: AuthState = {
  status: AuthenticationStatus.INITIAL,
  users: {},
  tokens: {},
  currentUserId: null,
  nbUnreadByUser: {},
  statusGetStream: AuthenticationStatusGetStream.LOADING,
  currentCallData: null,
  idsCallsIgnored: []
};

// Initialize state from localStorage
const storedUsers = loadUsersFromStorage();
Object.entries(storedUsers).forEach(([userId, userData]) => {
  initialState.users[userId] = userData.pleb;
  initialState.tokens[userId] = userData.tokens;
  // Set as current user if there's stored data (optional, depends on your needs)
  if(userId === localStorage.getItem(LOCAL_STORAGE_KEY_LAST_USER)) {
    initialState.currentUserId = userId;
    initialState.status = AuthenticationStatus.INITIAL;
  }
});

if(!initialState.currentUserId && Object.keys(initialState.users).length > 0) {
  initialState.currentUserId = Object.keys(initialState.users)[0];
  initialState.status = AuthenticationStatus.INITIAL;
}

interface PayloadAuthStatusPleb {
  status?: AuthenticationStatus;
  pleb: PlebData;
  tokens?: Credentials;
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthStatus: (state, action: PayloadAction<AuthenticationStatus>) => {
      state.status = action.payload;
    },
    setCurrentUserId: (state, action: PayloadAction<string>) => {
      state.currentUserId = action.payload;
      state.status = AuthenticationStatus.LOGGING_OUT;
      localStorage.setItem(LOCAL_STORAGE_KEY_LAST_USER, action.payload);
    },
    setAuthStatusPleb: (state, action: PayloadAction<PayloadAuthStatusPleb>) => {
      const { pleb, status, tokens } = action.payload;
      const userId = pleb.id;
      state.currentUserId = userId;
      state.users[userId] = pleb;

      if(tokens) {
        state.tokens[userId] = tokens;
      }
      if(status) {
        state.status = status;
      }
      
      // Save to localStorage
      saveUserToStorage(userId, { 
        pleb: state.users[userId], 
        tokens: state.tokens[userId] 
      });
    },
    logout: (state, action: PayloadAction<string>) => {
      const userId = action.payload;
      
      // Remove from localStorage
      removeUserFromStorage(userId);
      
      // Remove from state
      delete state.users[userId];
      delete state.tokens[userId];
      delete state.nbUnreadByUser[userId];

      if(Object.keys(state.users).length === 0) {
        state.currentUserId = null;
        state.status = AuthenticationStatus.LOGGING_OUT;
      } else {
        state.currentUserId = Object.keys(state.users)[0];
        state.status = AuthenticationStatus.LOGGING_OUT;
        localStorage.setItem(LOCAL_STORAGE_KEY_LAST_USER, state.currentUserId);
      }
    },
    setNbUnread: (state, action: PayloadAction<number>) => {
      if(state.currentUserId) {
        state.nbUnreadByUser[state.currentUserId] = action.payload;
      }
    },
    setStatusGetStream: (state, action: PayloadAction<AuthenticationStatusGetStream>) => {
      state.statusGetStream = action.payload;
    },
    setCurrentCallData: (state, action: PayloadAction<CallStreamData | null>) => {
      state.currentCallData = action.payload;
    },
    addIdsCallsIgnored: (state, action: PayloadAction<string>) => {
      state.idsCallsIgnored.push(action.payload);
    },
    removeIdsCallsIgnored: (state, action: PayloadAction<string>) => {
      state.idsCallsIgnored = state.idsCallsIgnored.filter(id => id !== action.payload);
    }
  }
});

export const { logout, setAuthStatusPleb, setNbUnread, setStatusGetStream, setAuthStatus, setCurrentUserId, setCurrentCallData, addIdsCallsIgnored, removeIdsCallsIgnored } = authSlice.actions;
export default authSlice.reducer; 
